import React, { useState, useMemo } from 'react';
import {
  Box,
  TextField,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
  Menu,
  MenuItem,
  Chip,
  Tooltip,
  Button,
} from '@mui/material';
import {
  Search as SearchIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Visibility as VisibilityIcon,
  PublishedWithChanges as DevolvidoIcon,
  Cached as DevolverIcon,
  CalendarToday as CalendarIcon,
  MoreVert as MoreVertIcon,
  FactCheck as FactCheckIcon,
} from '@mui/icons-material';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { romaneioService, Romaneio } from '../../services/romaneio';

const RomaneioTable: React.FC = () => {
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedRomaneio, setSelectedRomaneio] = useState<Romaneio | null>(null);

  // Buscar todos os romaneios
  const { data: romaneios = [], isLoading, error, refetch } = useQuery({
    queryKey: ['romaneios'],
    queryFn: romaneioService.getAll,
  });

  // Filtrar romaneios baseado na busca
  const filteredRomaneios = useMemo(() => {
    if (!search.trim()) {
      return romaneios;
    }

    const searchLower = search.toLowerCase();
    return romaneios.filter(romaneio => {
      // Busca por ID
      if (romaneio.id?.toString().includes(searchLower)) {
        return true;
      }
      
      // Busca por nome do promotor
      if (romaneio.nomePromotor?.toLowerCase().includes(searchLower)) {
        return true;
      }
      
      // Busca por nome da praça
      if (romaneio.nomePraca?.toLowerCase().includes(searchLower)) {
        return true;
      }
      
      // Busca por data de saída
      if (romaneio.dataSaidaString?.toLowerCase().includes(searchLower)) {
        return true;
      }
      
      // Busca por status devolvido/não devolvido
      if (searchLower === 'devolvido' && romaneio.devolvido) {
        return true;
      }
      if (searchLower === 'não devolvido' && !romaneio.devolvido) {
        return true;
      }
      
      return false;
    });
  }, [romaneios, search]);

  // Paginação dos dados filtrados
  const paginatedRomaneios = useMemo(() => {
    return filteredRomaneios.slice(
      page * rowsPerPage,
      page * rowsPerPage + rowsPerPage
    );
  }, [filteredRomaneios, page, rowsPerPage]);

  const handleMenuClick = (event: React.MouseEvent<HTMLElement>, romaneio: Romaneio) => {
    setAnchorEl(event.currentTarget);
    setSelectedRomaneio(romaneio);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedRomaneio(null);
  };

  const handleSearch = () => {
    // A busca é feita automaticamente pelo useMemo
  };

  const handleCadastro = () => {
    navigate('/romaneio/novo');
  };

  const handleVisualizar = (id: string) => {
    navigate(`/romaneio/visualizar/${id}`);
    handleMenuClose();
  };

  const handleEditar = (id: string) => {
    navigate(`/romaneio/editar/${id}`);
    handleMenuClose();
  };

  const handleDevolverItens = async (id: string) => {
    try {
      await romaneioService.devolverItensRomaneio(id);
      refetch(); // Atualizar a tabela
      handleMenuClose();
    } catch (error) {
      console.error('Erro ao devolver itens:', error);
    }
  };

  const handleAlterarDatas = (id: string) => {
    navigate(`/romaneio/alterar-datas/${id}`);
    handleMenuClose();
  };

  const handleConferencia = () => {
    navigate('/ConferenciaRomaneio');
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <Typography>Carregando...</Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <Typography color="error">Erro ao carregar os romaneios.</Typography>
      </Box>
    );
  }

  return (
    <Paper sx={{ width: '100%', overflow: 'hidden' }}>
      <Box sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <TextField
          placeholder="Filtrar"
          variant="outlined"
          size="small"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{ minWidth: 300 }}
          slotProps={{
            input: {
              startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />,
            },
          }}
        />
        
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="outlined"
            startIcon={<FactCheckIcon />}
            onClick={handleConferencia}
          >
            Conferência
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleCadastro}
            sx={{ backgroundColor: 'primary.main', '&:hover': { backgroundColor: 'primary.dark' } }}
          >
            Novo Romaneio
          </Button>
        </Box>
      </Box>

      <TableContainer>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 'bold' }}>Código</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Promotor</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Praça</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Data de Saída</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Data de Retorno</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Status</TableCell>
              <TableCell align="right" sx={{ fontWeight: 'bold' }}>Ações</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {paginatedRomaneios.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  <Typography sx={{ py: 4, color: 'text.secondary' }}>
                    {search ? 'Nenhum romaneio encontrado para o filtro informado.' : 'Nenhum romaneio cadastrado.'}
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              paginatedRomaneios.map((romaneio) => (
                <TableRow key={romaneio.id} hover>
                  <TableCell>{romaneio.id}</TableCell>
                  <TableCell>{romaneio.nomePromotor}</TableCell>
                  <TableCell>{romaneio.nomePraca}</TableCell>
                  <TableCell>{romaneio.dataSaidaString}</TableCell>
                  <TableCell>{romaneio.dataRetornoString}</TableCell>
                  <TableCell>
                    {romaneio.devolvido ? (
                      <Chip label="Devolvido" color="success" size="small" />
                    ) : (
                      <Chip label="Não Devolvido" color="warning" size="small" />
                    )}
                  </TableCell>
                  <TableCell align="right">
                    <IconButton
                      onClick={(e) => handleMenuClick(e, romaneio)}
                      size="small"
                    >
                      <MoreVertIcon />
                    </IconButton>
                    
                    <Menu
                      anchorEl={anchorEl}
                      open={Boolean(anchorEl) && selectedRomaneio?.id === romaneio.id}
                      onClose={handleMenuClose}
                    >
                      <MenuItem onClick={() => handleVisualizar(romaneio.id)}>
                        <VisibilityIcon sx={{ mr: 1, fontSize: 20 }} />
                        Visualizar
                      </MenuItem>
                      <MenuItem onClick={() => handleEditar(romaneio.id)}>
                        <EditIcon sx={{ mr: 1, fontSize: 20 }} />
                        Editar
                      </MenuItem>
                      {romaneio.devolvido ? (
                        <MenuItem disabled>
                          <DevolvidoIcon sx={{ mr: 1, fontSize: 20 }} />
                          Itens já devolvidos
                        </MenuItem>
                      ) : (
                        <MenuItem onClick={() => handleDevolverItens(romaneio.id)}>
                          <DevolverIcon sx={{ mr: 1, fontSize: 20 }} />
                          Devolver itens
                        </MenuItem>
                      )}
                      <MenuItem onClick={() => handleAlterarDatas(romaneio.id)}>
                        <CalendarIcon sx={{ mr: 1, fontSize: 20 }} />
                        Alterar datas
                      </MenuItem>
                    </Menu>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        rowsPerPageOptions={[10, 25, 50, 100]}
        component="div"
        count={filteredRomaneios.length}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        labelRowsPerPage="Linhas por página:"
        labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count} itens`}
        sx={{
          borderTop: '1px solid',
          borderColor: 'divider',
          '.MuiTablePagination-toolbar': { pl: 2, pr: 2 }
        }}
      />
    </Paper>
  );
};

export default RomaneioTable;
